import chunk from "lodash/chunk";
import cloneDeep from "lodash/cloneDeep";
import flattenDeep from "lodash/flattenDeep";
import fpSet from "lodash/fp/set";
import get from "lodash/get";
import isArray from "lodash/isArray";
import isPlainObject from "lodash/isPlainObject";
import map from "lodash/map";
import remove from "lodash/remove";
import toPath from "lodash/toPath";

export class TreeModifier {
  /**
   * Get the list of all sortable nodes (by paths) in the tree
   *
   * @param {object} tree
   * @param {string} sortableContainerKey Key of the sortable container
   * @param {string} parentKey (for recursion)
   * @return {array} list of sortable paths
   */
  static getSortablePaths(tree, sortableContainerKey, parentKey) {
    let result = [];
    if (Array.isArray(tree)) {
      result = result.concat(
        tree.map((value, index) =>
          TreeModifier.getSortablePaths(
            value,
            sortableContainerKey,
            `${parentKey}[${index}]`
          )
        )
      );
    } else if (isPlainObject(tree)) {
      result = result.concat(
        Object.keys(tree).map((key) => {
          const path = parentKey ? `${parentKey}.${key}` : `${key}`;
          if (sortableContainerKey == key && Array.isArray(tree[key])) {
            // Add paths for elements inside the sortable container
            return tree[key].map((_value, index) => `${path}[${index}]`);
          } else {
            return TreeModifier.getSortablePaths(
              tree[key],
              sortableContainerKey,
              path
            );
          }
        })
      );
    } else {
      result = [];
    }
    return flattenDeep(result);
  }

  /**
   * Modify the tree (move or add the node)
   *
   * @param {object} tree
   * @param {object} node
   * @param {string} initialPath
   * @param {string} destinationPath
   * @return {object} modified tree
   */
  // TODO IMP-1216 fix max-params
  // eslint-disable-next-line max-params
  static modify(tree, node, initialPath, destinationPath) {
    if (initialPath && destinationPath) {
      return TreeModifier.move(tree, node, initialPath, destinationPath);
    }
    return tree;
  }

  /**
   * Move the node from a path to another
   *
   * @param {object} tree
   * @param {object} node
   * @param {string} initialPath
   * @param {string} destinationPath
   * @return {object} modified tree
   */
  // TODO IMP-1216 fix max-params
  // eslint-disable-next-line max-params
  static move(tree, node, initialPath, destinationPath) {
    const temp = cloneDeep(tree);

    if (Array.isArray(get(temp, destinationPath))) {
      destinationPath += `[0]`; // Place by default as first element
    }
    // Extract positions
    const initialParentPath = toPath(initialPath);
    const initialIndex = initialParentPath.pop();
    const destinationParentPath = toPath(destinationPath);
    const destinationIndex = destinationParentPath.pop();
    // Get initial and destination field arrays
    const initial = get(temp, initialParentPath);
    const destination = get(temp, destinationParentPath);

    if (!node || !destination || !initial || node == destination) {
      return tree; // Invalid move
    }

    // Modify
    initial.splice(initialIndex, 1); // Remove from initial array
    destination.splice(destinationIndex, 0, node); // Insert into destination array

    return temp;
  }

  /**
   * Find a node in the tree
   *
   * @param {object} tree
   * @param {string} nodeContainerKey
   * @param {function} predicate
   * @return {object} found node
   */
  static find(tree, nodeContainerKey, predicate) {
    if (Array.isArray(tree)) {
      for (const branch of tree) {
        const node = this.find(branch, nodeContainerKey, predicate);
        if (node) return node;
      }
      return null;
    }

    if (!isPlainObject(tree)) return null;

    if (predicate(tree)) return tree;

    const children = tree[nodeContainerKey];
    if (!children) return null;

    for (const branch of children) {
      const node = this.find(branch, nodeContainerKey, predicate);
      if (node) return node;
    }

    return null;
  }

  /**
   * Find a node parent in the tree
   *
   * @param {object} tree
   * @param {string} nodeContainerKey
   * @param {function} predicate
   * @return {object} found parent node
   */
  static findParentNode(tree, nodeContainerKey, predicate) {
    // root node has no parent
    if (predicate(tree)) {
      return tree;
    }

    if (tree[nodeContainerKey]) {
      const childNode = this.getPath(tree, nodeContainerKey, predicate);
      if (!childNode?.node) {
        return tree;
      }

      // remove last chunk of the child node path to get its parent path
      const parentPath = chunk(childNode.path, 2);
      parentPath.pop();

      // follow path to parent node
      let parentNode = tree;
      for (let i = 0; i < parentPath.length; i++) {
        parentNode = parentNode[parentPath[i][0]][parentPath[i][1]];
      }

      return parentNode;
    }

    return null;
  }

  /**
   * Find a node path in the tree
   * @param {object} tree
   * @param {string} nodeContainerKey
   * @param {function} predicate
   * @returns {node and path}
   */
  // TODO IMP-1216 fix max-params
  // eslint-disable-next-line max-params
  static getPath(tree, nodeContainerKey, predicate, throughPath = []) {
    // check if right node
    if (predicate(tree)) {
      return { node: tree, path: throughPath };
    }

    // dive into tree["templates"]
    if (tree[nodeContainerKey]) {
      throughPath.push(nodeContainerKey);

      // over all templates
      for (let i = 0; i < tree[nodeContainerKey].length; i++) {
        const selfPath = [...throughPath];
        selfPath.push(i);

        const obj = this.getPath(
          tree[nodeContainerKey][i],
          nodeContainerKey,
          predicate,
          selfPath
        );

        if (obj?.node) {
          return { node: obj.node, path: obj.path };
        }
      }
    }
    return null;
  }

  /**
   * Find and replace a node in the tree
   * @param {object} tree
   * @param {string} nodeContainerKey
   * @param {function} callBack
   * @returns {tree, path, node}
   */
  // TODO IMP-1216 fix max-params
  // eslint-disable-next-line max-params
  static findAndReplace(tree, nodeContainerKey, predicate, newNode) {
    const nodeToReplace = this.getPath(tree, nodeContainerKey, predicate);

    // found
    if (nodeToReplace?.node) {
      return fpSet(nodeToReplace.path, newNode, tree);
    } else {
      return null;
    }
  }

  /**
   * Find and append a child node
   * @param {object} tree
   * @param {string} nodeContainerKey
   * @param {function} callBack
   * @param {object} childNode
   * @returns {{tree, path, node}}
   */
  // TODO IMP-1216 fix max-params
  // eslint-disable-next-line max-params
  static findAndAppendChild(tree, nodeContainerKey, predicate, childNode) {
    const newTree = cloneDeep(tree);
    const parentNode = this.getPath(newTree, nodeContainerKey, predicate);

    if (!parentNode?.node) {
      return tree;
    }

    if (isArray(parentNode.node[nodeContainerKey])) {
      parentNode.node[nodeContainerKey].push(childNode);
    } else {
      parentNode.node[nodeContainerKey] = [childNode];
    }

    return newTree;
  }

  /**
   * Find and remove a node
   * @param {object} tree
   * @param {string} nodeContainerKey
   * @param {function} callBack
   *
   * @returns {object}
   */
  static findAndRemove(tree, nodeContainerKey, predicate) {
    const newTree = cloneDeep(tree);

    if (isArray(newTree[nodeContainerKey])) {
      newTree[nodeContainerKey] = remove(
        map(newTree[nodeContainerKey], (childNode) =>
          this.findAndRemove(childNode, nodeContainerKey, predicate)
        ),
        (childNode) => !predicate(childNode)
      );
    }

    return newTree;
  }

  static deepReplace({ node, name = "", replacer = (_key, value) => value }) {
    if (Array.isArray(node)) {
      return node.map((item) =>
        this.deepReplace({ node: item, name, replacer })
      );
    } else if (typeof node === "object" && node !== null) {
      const transformed = {};
      for (const key in node) {
        if (key === name) {
          transformed[key] = replacer(key, node[key]);
        } else {
          transformed[key] = this.deepReplace({
            node: node[key],
            name,
            replacer,
          });
        }
      }

      return transformed;
    } else {
      return node; // return primitive value "as is"
    }
  }
}
